import { capitalize, Stack } from '@mui/material'
import Typography from '@mui/material/Typography'
import { CalendarStatus } from '@prisma/client'
import { isSameDay } from 'date-fns'
import { String } from 'effect'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import IconeGroup from 'assets/icons/event/groups.svg'
import IconeLocation from 'assets/icons/event/location.svg'
import CalendarIcon from 'assets/icons/nucleo/18px_calendar.svg'
import { NextLinkWithoutDecoration } from 'components/common/Link'
import { LocaleContext } from 'components/common/LocaleContext'
import { MarkdownDisplay } from 'components/common/MarkdownDisplay/MarkdownDisplay'
import { formatEditionDateWithHours } from 'components/common/date.utils'
import dataParsing from 'dataParsing/fr'
import { raceCategoriesLevel1 } from 'helpers/raceCategoryLevels'
import { replaceMarkdownEmptyParagraphs } from 'helpers/string'
import { PreviousEditionAttendees } from 'pages/event/[...slugs]'
import { OpenContestEdition } from 'services/api/database/ContestService'
import { unifiedColors } from 'styles/colors'

import {
  CapsDateEventContent,
  CapsEventContent,
  CapsIconGroupEventContent,
  CapsIconPlaceEventContent,
  EditorEventContent,
  GridEventContent,
  InfoEventContent
} from './map/EventContent.style'

interface InfoCourseProps {
  previousEditionAttendeesCount: PreviousEditionAttendees | null
  city: string
  countrySubdivisionDisplayCodeLevel2: string
  countrySubdivisionNameLevel2: string
  calendarStatus: CalendarStatus
  startDate: Date | null
  endDate: Date | null
  contest: OpenContestEdition | null
  peyceReview: string
  editionStart: Date | null
  editionEnd: Date | null
  timeZone: string
  isOver: boolean
  categoryLevel1: string | null | undefined
}

const InfoCourse = ({
  calendarStatus,
  startDate,
  endDate,
  timeZone,
  city,
  countrySubdivisionDisplayCodeLevel2,
  countrySubdivisionNameLevel2,
  peyceReview,
  editionStart,
  editionEnd,
  isOver,
  categoryLevel1,
  previousEditionAttendeesCount
}: InfoCourseProps) => {
  const { dateStringLocale } = useContext(LocaleContext)
  const isToBeConfirmed = calendarStatus === CalendarStatus.TO_BE_CONFIRMED
  const longFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone
  }
  const formatOptions: Intl.DateTimeFormatOptions = isToBeConfirmed
    ? { month: 'long', year: 'numeric', timeZone }
    : longFormatOptions
  const { t } = useTranslation(['event', 'common'])
  const typeUrl = categoryLevel1
    ? raceCategoriesLevel1[categoryLevel1 as keyof typeof raceCategoriesLevel1]
        .url
    : ''
  const parsedLocation = Object.entries(
    dataParsing.locations.countrySubdivisionLevel2
  ).find(([, value]) => value === countrySubdivisionNameLevel2)?.[0]

  const formattedStartDate = startDate
    ? startDate.toLocaleDateString(dateStringLocale, formatOptions)
    : null
  const formattedEndDate = endDate
    ? endDate.toLocaleDateString(dateStringLocale, formatOptions)
    : null

  const isStartSameEnd = startDate && endDate && isSameDay(startDate, endDate)
  const [
    formattedEditionStartDate,
    formattedEditionStartHours,
    formattedEditionEndDate,
    formattedEditionEndHours
  ] = [editionStart, editionEnd].flatMap(
    formatEditionDateWithHours(dateStringLocale)
  )

  return (
    <>
      <GridEventContent>
        <InfoEventContent>
          <CapsEventContent>
            {startDate && (
              <CapsDateEventContent>
                <Typography
                  component="div"
                  variant="bodySRegular"
                  color={unifiedColors.label.subdued}
                  textTransform="capitalize"
                >
                  {startDate.toLocaleDateString(dateStringLocale, {
                    month: 'short',
                    timeZone
                  })}
                </Typography>
                <Typography
                  component="div"
                  variant="bodyLBold"
                  color={unifiedColors.label.subdued}
                >
                  {isToBeConfirmed
                    ? '?'
                    : startDate.toLocaleDateString(dateStringLocale, {
                        day: 'numeric',
                        timeZone
                      })}
                </Typography>
              </CapsDateEventContent>
            )}
          </CapsEventContent>
          <div>
            <Typography
              component="div"
              variant="bodySRegular"
              color={unifiedColors.label.subdued}
              fontWeight={600}
              fontStyle={'italic'}
              textTransform={'uppercase'}
            >
              {t('date')}
            </Typography>
            <Typography component="div" variant="body1" fontWeight={600}>
              {(isStartSameEnd || isToBeConfirmed) && formattedStartDate ? (
                <span suppressHydrationWarning>
                  {String.capitalize(formattedStartDate)}
                </span>
              ) : (
                <span suppressHydrationWarning>
                  {String.capitalize(
                    t('rangeDate', {
                      startDate: formattedStartDate,
                      endDate: formattedEndDate
                    })
                  )}
                </span>
              )}
            </Typography>
            <Typography
              component="div"
              variant="bodyLRegular"
              color={unifiedColors.label.subdued}
            >
              {isOver
                ? t('common:event.footer.over')
                : calendarStatus
                ? t(`common:${calendarStatus}`)
                : ''}
            </Typography>
          </div>
        </InfoEventContent>
        <InfoEventContent>
          <CapsEventContent>
            <CapsIconPlaceEventContent>
              <IconeLocation />
            </CapsIconPlaceEventContent>
          </CapsEventContent>
          <div>
            <Typography
              component="div"
              variant="bodySSemiBold"
              color={unifiedColors.label.subdued}
              fontStyle={'italic'}
              textTransform={'uppercase'}
            >
              {t('place')}
            </Typography>
            <Typography component="div" variant="body1" fontWeight={600}>
              {city ? city : ''}
            </Typography>
            {typeUrl && parsedLocation && (
              <NextLinkWithoutDecoration
                href={`/${typeUrl}/${parsedLocation}`}
                passHref
              >
                <Typography
                  component="div"
                  variant="bodyLRegular"
                  color={unifiedColors.label.subdued}
                >
                  {countrySubdivisionDisplayCodeLevel2 ||
                  countrySubdivisionNameLevel2
                    ? ''
                    : null}
                  {countrySubdivisionDisplayCodeLevel2
                    ? `${countrySubdivisionDisplayCodeLevel2} - `
                    : ''}
                  {countrySubdivisionNameLevel2
                    ? countrySubdivisionNameLevel2
                    : ''}
                </Typography>
              </NextLinkWithoutDecoration>
            )}
          </div>
        </InfoEventContent>
        {(formattedEditionStartDate || formattedEditionEndDate) && (
          <InfoEventContent>
            <CapsEventContent>
              <CapsIconPlaceEventContent>
                <CalendarIcon />
              </CapsIconPlaceEventContent>
            </CapsEventContent>
            <div>
              <Typography
                component="div"
                variant="bodySRegular"
                color={unifiedColors.label.subdued}
                fontWeight={600}
                fontStyle={'italic'}
                textTransform={'uppercase'}
              >
                {t('registrations')}
              </Typography>
              <Stack>
                <Stack>
                  {formattedEditionStartDate && (
                    <Stack>
                      <Typography fontWeight={600} suppressHydrationWarning>
                        {t('opensOn', {
                          date: capitalize(formattedEditionStartDate)
                        })}
                      </Typography>
                      <Typography color={unifiedColors.label.subdued}>
                        {t('at', { time: formattedEditionStartHours })}
                      </Typography>
                    </Stack>
                  )}
                  {formattedEditionEndDate && (
                    <Stack>
                      <Typography fontWeight={600} suppressHydrationWarning>
                        {t('closesOn', {
                          date: capitalize(formattedEditionEndDate)
                        })}
                      </Typography>
                      <Typography color={unifiedColors.label.subdued}>
                        {t('at', { time: formattedEditionEndHours })}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </div>
          </InfoEventContent>
        )}
        {previousEditionAttendeesCount && (
          <InfoEventContent>
            <CapsEventContent>
              <CapsIconGroupEventContent>
                <IconeGroup />
              </CapsIconGroupEventContent>
            </CapsEventContent>
            <div>
              <Typography component="div" variant="body1" fontWeight={600}>
                {t('participant', {
                  count: previousEditionAttendeesCount.count
                })}
              </Typography>
              <Typography
                component="div"
                variant="bodyLRegular"
                color={unifiedColors.label.subdued}
              >
                {t('in')} {previousEditionAttendeesCount.editionYear}
              </Typography>
            </div>
          </InfoEventContent>
        )}
        <EditorEventContent>
          <EditorEventContent>
            {peyceReview && (
              <MarkdownDisplay>
                {replaceMarkdownEmptyParagraphs(peyceReview)}
              </MarkdownDisplay>
            )}
          </EditorEventContent>
        </EditorEventContent>
      </GridEventContent>
    </>
  )
}

export default InfoCourse
